/*
 * External Entities workspace.
 *
 * A tenant-wide directory hub covering the six live entity tabs plus their
 * companion Report tabs. Reuses existing modules where they already exist
 * (Provider Directory, Insurance Companies, Family Members) and layers three
 * new tenant-wide entities on top (Community Partners, Collateral Contacts,
 * and Referral Resources).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Empty } from "../components/UI";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea, CheckBox } from "./engagement/FormControls";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Plus, Search, Pencil, Trash2, Users, Building2, Handshake, Phone, MapPin,
         BookOpenCheck, Landmark, HeartPulse, ClipboardList, Info } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "assigned-providers",  label: "Assigned Providers",         icon: Users,          kind: "report" },
  { key: "care-providers",      label: "Care Providers",             icon: Handshake,      kind: "provider-directory" },
  { key: "collateral-contacts", label: "Collateral Contacts",        icon: Phone,          kind: "collateral-contacts" },
  { key: "community-partners",  label: "Community Partners",         icon: Building2,      kind: "community-partners" },
  { key: "family",              label: "Family",                     icon: HeartPulse,     kind: "family" },
  { key: "referral-resources",  label: "Referral Resources",         icon: BookOpenCheck,  kind: "referral-resources" },
  { key: "care-providers-report",        label: "Care Providers Report",        icon: ClipboardList, kind: "report" },
  { key: "collateral-contacts-report",   label: "Collateral Contacts Report",   icon: ClipboardList, kind: "report" },
  { key: "community-partners-report",    label: "Community Partners Report",    icon: ClipboardList, kind: "report" },
  { key: "family-contact-report",        label: "Family Contact Report",        icon: ClipboardList, kind: "report" },
  { key: "insurance-companies-report",   label: "Insurance Company Report",     icon: Landmark,      kind: "report" },
  { key: "referral-resources-report",    label: "Referral Resources Report",    icon: ClipboardList, kind: "report" },
];

const PARTNER_TYPES = [
  "Housing", "Shelter", "Food Assistance", "Government Agency",
  "Family Services", "Substance Use Treatment", "Mental Health",
  "Legal Services", "Vocational", "Faith-Based", "Peer Support",
  "Advocacy", "Education", "Transportation", "Medical", "Other",
];

const COLLATERAL_ROLES = [
  "Court Liaison", "Probation Officer", "Parole Officer", "Attorney",
  "School Counselor", "Teacher", "Consulting Physician", "Nurse",
  "Guardian ad Litem", "Case Worker", "Landlord", "Employer",
  "Faith Leader", "Sponsor", "Other",
];

const SERVICE_TYPES = [
  "Substance Use Outpatient", "Substance Use Residential", "Detox and Inpatient Residential",
  "MAT and OP Treatment", "Mental Health Outpatient", "Crisis Stabilization",
  "Child and Family Therapy", "Peer Support", "Case Management",
  "Housing Support", "Vocational Rehabilitation", "Legal Aid",
  "Food and Basic Needs", "LGBTQ+ Youth Support", "Faith-Based Recovery",
  "Domestic Violence", "Other",
];

const EMPTY_CP = { agency_title: "", type_of_partner: "", contact_person: "", agency_name: "",
                   agency_address: "", agency_website: "", business_phone: "", cell_phone: "",
                   email: "", notes: "" };
const EMPTY_CC = { contact_name: "", relationship: "", organization: "", role: "",
                   phone: "", email: "", address: "", release_of_info_on_file: false, notes: "" };
const EMPTY_RR = { resource_name: "", service_type: "", contact_person: "", phone: "", email: "",
                   website: "", address: "", in_network: false, accepts_medicaid: false, notes: "" };

function TabButton({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button type="button" data-testid={`ee-tab-${tab.key}`} onClick={onClick}
      className={`whitespace-nowrap inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${active ? "bg-[#4E342E] text-white shadow-[0_4px_12px_rgba(78,52,46,0.25)]" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
      <Icon size={14} /> {tab.label}
    </button>
  );
}

function Toolbar({ search, setSearch, canEdit, onAdd, addLabel, count, onSeed, seeding }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="text-sm text-[#7B6157]">
        Showing <span className="font-semibold text-[#3E2723]">{count}</span> record{count === 1 ? "" : "s"}
      </div>
      <div className="relative ml-auto">
        <Search size={15} className="absolute left-3 top-3 text-[#84675B]" />
        <FInput data-testid="ee-search" placeholder="Search here"
                className="pl-9 h-10 w-64" value={search}
                onChange={(e) => setSearch(e.target.value)} />
      </div>
      {onSeed && (
        <Btn variant="outline" data-testid="ee-seed-btn" onClick={onSeed} disabled={seeding}>
          {seeding ? "Seeding..." : "Seed Demo Data"}
        </Btn>
      )}
      {canEdit && onAdd && (
        <Btn data-testid="ee-add-btn" onClick={onAdd}><Plus size={16} /> {addLabel}</Btn>
      )}
    </div>
  );
}

function DataTable({ headers, rows, empty }) {
  if (!rows.length) return <Empty text={empty || "No records yet."} />;
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#EFEBE0]">
      <table className="w-full text-sm text-left" data-testid="ee-data-table">
        <thead>
          <tr className="bg-[#F5F2EB]">
            {headers.map((h) => (
              <th key={h.key || h.label} className="sticky top-0 bg-[#F5F2EB] text-[#6D4C41] font-semibold py-3 px-4 text-xs uppercase tracking-wide">
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r._id || r.id || i} className="hover:bg-[#FDFCF8]">
              {headers.map((h) => (
                <td key={h.key || h.label} className={`border-b border-[#EFEBE0] py-3 px-4 text-[#4E342E] ${h.className || ""}`}>
                  {h.render ? h.render(r) : (r[h.key] || <span className="text-[#84675B]">-</span>)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============ Community Partners Form ============ */
function CommunityPartnerForm({ form, setForm, onSubmit, busy }) {
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><FLabel>Agency Title</FLabel>
          <FInput data-testid="cp-agency-title" value={form.agency_title} onChange={f("agency_title")} placeholder="e.g., Housing First Program" />
        </div>
        <div><FLabel>Type of Partner</FLabel>
          <FSelect data-testid="cp-type-of-partner" value={form.type_of_partner} onChange={f("type_of_partner")}>
            <option value="">Select here</option>
            {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </FSelect>
        </div>
        <div><FLabel>Contact Person</FLabel>
          <FInput data-testid="cp-contact-person" value={form.contact_person} onChange={f("contact_person")} />
        </div>
        <div><FLabel>Agency Name *</FLabel>
          <FInput data-testid="cp-agency-name" value={form.agency_name} onChange={f("agency_name")} required />
        </div>
        <div className="col-span-2"><FLabel>Agency Address</FLabel>
          <FInput data-testid="cp-agency-address" value={form.agency_address} onChange={f("agency_address")} />
        </div>
        <div><FLabel>Agency Website</FLabel>
          <FInput data-testid="cp-agency-website" value={form.agency_website} onChange={f("agency_website")} placeholder="https://" />
        </div>
        <div><FLabel>Business Phone</FLabel>
          <FInput data-testid="cp-business-phone" value={form.business_phone} onChange={f("business_phone")} />
        </div>
        <div><FLabel>Cell Phone</FLabel>
          <FInput data-testid="cp-cell-phone" value={form.cell_phone} onChange={f("cell_phone")} />
        </div>
        <div><FLabel>Email Address</FLabel>
          <FInput type="email" data-testid="cp-email" value={form.email} onChange={f("email")} />
        </div>
        <div className="col-span-2"><FLabel>Notes</FLabel>
          <FTextarea data-testid="cp-notes" value={form.notes} onChange={f("notes")} rows={3} />
        </div>
      </div>
      <div className="flex justify-end pt-2">
        <Btn type="submit" data-testid="cp-save-btn" disabled={busy}>
          {busy ? "Saving..." : "Save"}
        </Btn>
      </div>
    </form>
  );
}

/* ============ Collateral Contact Form ============ */
function CollateralContactForm({ form, setForm, onSubmit, busy }) {
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><FLabel>Contact Name *</FLabel>
          <FInput data-testid="cc-name" value={form.contact_name} onChange={f("contact_name")} required />
        </div>
        <div><FLabel>Relationship</FLabel>
          <FInput data-testid="cc-relationship" value={form.relationship} onChange={f("relationship")} placeholder="e.g., Court Liaison, School Counselor" />
        </div>
        <div><FLabel>Organization</FLabel>
          <FInput data-testid="cc-organization" value={form.organization} onChange={f("organization")} />
        </div>
        <div><FLabel>Role or Title</FLabel>
          <FSelect data-testid="cc-role" value={form.role} onChange={f("role")}>
            <option value="">Select here</option>
            {COLLATERAL_ROLES.map((t) => <option key={t} value={t}>{t}</option>)}
          </FSelect>
        </div>
        <div><FLabel>Phone</FLabel>
          <FInput data-testid="cc-phone" value={form.phone} onChange={f("phone")} />
        </div>
        <div><FLabel>Email</FLabel>
          <FInput type="email" data-testid="cc-email" value={form.email} onChange={f("email")} />
        </div>
        <div className="col-span-2"><FLabel>Address</FLabel>
          <FInput data-testid="cc-address" value={form.address} onChange={f("address")} />
        </div>
        <div className="col-span-2">
          <CheckBox testId="cc-roi"
                    checked={!!form.release_of_info_on_file}
                    onChange={() => setForm((s) => ({ ...s, release_of_info_on_file: !s.release_of_info_on_file }))}
                    label="Release of Information on file" />
        </div>
        <div className="col-span-2"><FLabel>Notes</FLabel>
          <FTextarea data-testid="cc-notes" value={form.notes} onChange={f("notes")} rows={3} />
        </div>
      </div>
      <div className="flex justify-end pt-2">
        <Btn type="submit" data-testid="cc-save-btn" disabled={busy}>
          {busy ? "Saving..." : "Save"}
        </Btn>
      </div>
    </form>
  );
}

/* ============ Referral Resource Form ============ */
function ReferralResourceForm({ form, setForm, onSubmit, busy }) {
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><FLabel>Resource Name *</FLabel>
          <FInput data-testid="rr-name" value={form.resource_name} onChange={f("resource_name")} required />
        </div>
        <div><FLabel>Service Type</FLabel>
          <FSelect data-testid="rr-service-type" value={form.service_type} onChange={f("service_type")}>
            <option value="">Select here</option>
            {SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </FSelect>
        </div>
        <div><FLabel>Contact Person</FLabel>
          <FInput data-testid="rr-contact" value={form.contact_person} onChange={f("contact_person")} />
        </div>
        <div><FLabel>Phone</FLabel>
          <FInput data-testid="rr-phone" value={form.phone} onChange={f("phone")} />
        </div>
        <div><FLabel>Email</FLabel>
          <FInput type="email" data-testid="rr-email" value={form.email} onChange={f("email")} />
        </div>
        <div><FLabel>Website</FLabel>
          <FInput data-testid="rr-website" value={form.website} onChange={f("website")} placeholder="https://" />
        </div>
        <div className="col-span-2"><FLabel>Address</FLabel>
          <FInput data-testid="rr-address" value={form.address} onChange={f("address")} />
        </div>
        <div>
          <CheckBox testId="rr-in-network"
                    checked={!!form.in_network}
                    onChange={() => setForm((s) => ({ ...s, in_network: !s.in_network }))}
                    label="In-Network" />
        </div>
        <div>
          <CheckBox testId="rr-medicaid"
                    checked={!!form.accepts_medicaid}
                    onChange={() => setForm((s) => ({ ...s, accepts_medicaid: !s.accepts_medicaid }))}
                    label="Accepts Medicaid" />
        </div>
        <div className="col-span-2"><FLabel>Notes</FLabel>
          <FTextarea data-testid="rr-notes" value={form.notes} onChange={f("notes")} rows={3} />
        </div>
      </div>
      <div className="flex justify-end pt-2">
        <Btn type="submit" data-testid="rr-save-btn" disabled={busy}>
          {busy ? "Saving..." : "Save"}
        </Btn>
      </div>
    </form>
  );
}

/* ============ Reusable CRUD Section for the three new entities ============ */
function CrudEntity({ tabKey, endpoint, addLabel, emptyForm, seed, FormComponent, tableHeaders, isAdmin, canEditFlag }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = () => api.get(endpoint, { params: { q: search || undefined } })
    .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [search]);

  const openAdd = () => { setForm(emptyForm); setEditId(null); setPanel(true); };
  const openEdit = (r) => {
    const merged = { ...emptyForm };
    Object.keys(emptyForm).forEach((k) => {
      if (r[k] !== undefined && r[k] !== null) merged[k] = r[k];
    });
    setForm(merged); setEditId(r.id); setPanel(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`${endpoint}/${editId}`, form);
      else await api.post(endpoint, form);
      toast.success(editId ? "Record updated" : "Record added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`${endpoint}/${confirmDel.id}`);
      toast.success("Record deleted");
      setConfirmDel(null); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/external-entities/seed-demo");
      const c = r.data?.created || {};
      const total = Object.values(c).reduce((a, b) => a + b, 0);
      toast.success(total ? `Seeded ${total} demo record${total === 1 ? "" : "s"}` : "Demo records already present");
      load();
    } catch (err) { toast.error(apiError(err)); }
    setSeeding(false);
  };

  const actionHeader = canEditFlag ? [{
    key: "_actions", label: "Actions", className: "w-24",
    render: (r) => (
      <div className="flex gap-2">
        <button type="button" data-testid={`ee-edit-${r.id}`} onClick={() => openEdit(r)}
                aria-label="Edit"
                className="h-8 w-8 rounded-full text-[#6D4C41] hover:bg-[#F5F2EB] flex items-center justify-center">
          <Pencil size={14} />
        </button>
        {isAdmin && (
          <button type="button" data-testid={`ee-delete-${r.id}`} onClick={() => setConfirmDel(r)}
                  aria-label="Delete"
                  className="h-8 w-8 rounded-full text-[#C62828] hover:bg-[#FFEBEE] flex items-center justify-center">
            <Trash2 size={14} />
          </button>
        )}
      </div>
    ),
  }] : [];

  return (
    <div>
      <Toolbar search={search} setSearch={setSearch}
               canEdit={canEditFlag}
               onAdd={openAdd} addLabel={addLabel}
               count={rows.length}
               onSeed={isAdmin && seed ? seedDemo : null} seeding={seeding} />
      <Card className="p-0">
        <DataTable headers={[...tableHeaders, ...actionHeader]} rows={rows}
                   empty={`No ${addLabel.replace(/^(Create New |Add New )/i, "").toLowerCase()} records yet.`} />
      </Card>

      <SlidePanel open={panel} onClose={() => setPanel(false)}
                  title={editId ? `Edit ${addLabel.replace(/^Create New |^Add New /i, "")}` : addLabel}
                  width="max-w-2xl"
                  testId={`ee-panel-${tabKey}`}>
        <FormComponent form={form} setForm={setForm} onSubmit={save} busy={busy} />
      </SlidePanel>

      <ConfirmDialog open={!!confirmDel} onClose={() => setConfirmDel(null)}
                     onConfirm={doDelete} busy={busy}
                     title="Delete this record"
                     description="This will remove the entry from the tenant-wide directory."
                     confirmWord="DELETE" confirmLabel="Delete" />
    </div>
  );
}

/* ============ Report-only tab (read-only tenant roll-up) ============ */
function ReportTab({ endpoint, tableHeaders, addLabel, tabKey }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const load = () => api.get(endpoint, { params: { q: search || undefined } })
    .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [search]);

  return (
    <div>
      <Toolbar search={search} setSearch={setSearch} canEdit={false} count={rows.length} />
      <Card className="p-0">
        <DataTable headers={tableHeaders} rows={rows}
                   empty="No records in this report yet." />
      </Card>
    </div>
  );
}

/* ============ Family tab: pick client, then show family members ============ */
function FamilyTab({ canEditFlag }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [rows, setRows] = useState([]);
  const [rollup, setRollup] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState(false);
  const EMPTY_FAM = { first_name: "", last_name: "", relationship: "", phone: "", email: "",
                      address: "", emergency_contact: false, legal_guardian: false,
                      release_of_info: false, notes: "" };
  const [form, setForm] = useState(EMPTY_FAM);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/external-entities/family-report").then((r) => setRollup(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!clientId) { setRows([]); return; }
    api.get(`/clients/${clientId}/family`)
      .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [clientId]);

  const filteredRollup = useMemo(() => {
    if (!search) return rollup;
    const s = search.toLowerCase();
    return rollup.filter((r) =>
      `${r.client_name} ${r.first_name} ${r.last_name} ${r.relationship} ${r.phone} ${r.email}`
        .toLowerCase().includes(s));
  }, [rollup, search]);

  const openAdd = () => { setForm(EMPTY_FAM); setEditId(null); setPanel(true); };
  const openEdit = (r) => {
    const merged = { ...EMPTY_FAM };
    Object.keys(EMPTY_FAM).forEach((k) => { if (r[k] !== undefined) merged[k] = r[k]; });
    setForm(merged); setEditId(r.id); setPanel(true);
  };
  const save = async (e) => {
    e.preventDefault();
    if (!clientId) { toast.error("Pick a client first"); return; }
    setBusy(true);
    try {
      if (editId) await api.put(`/family/${editId}`, form);
      else await api.post(`/clients/${clientId}/family`, form);
      toast.success(editId ? "Family member updated" : "Family member added");
      setPanel(false);
      const r = await api.get(`/clients/${clientId}/family`);
      setRows(r.data);
      const roll = await api.get("/external-entities/family-report");
      setRollup(roll.data);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const clientLabel = (c) => `${c.last_name || ""}, ${c.first_name || ""}`.replace(/^,\s*/, "").trim() || "Unnamed";

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[260px]">
            <FLabel>Pick a client to manage family contacts</FLabel>
            <FSelect data-testid="ee-family-client" value={clientId}
                     onChange={(e) => setClientId(e.target.value)}>
              <option value="">Select here</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{clientLabel(c)}</option>
              ))}
            </FSelect>
          </div>
          {clientId && canEditFlag && (
            <Btn data-testid="ee-family-add" onClick={openAdd}><Plus size={16} /> Add Family Member</Btn>
          )}
          <div className="ml-auto text-sm text-[#7B6157] flex items-center gap-1.5">
            <Info size={14} /> Tenant roll-up: <span className="font-semibold text-[#3E2723]">{rollup.length}</span> family contact{rollup.length === 1 ? "" : "s"}
          </div>
        </div>
      </Card>

      {clientId ? (
        <Card className="p-0">
          <DataTable rows={rows}
            headers={[
              { key: "first_name", label: "First Name" },
              { key: "last_name", label: "Last Name" },
              { key: "relationship", label: "Relationship" },
              { key: "phone", label: "Phone" },
              { key: "email", label: "Email" },
              { key: "address", label: "Address" },
              { key: "_flags", label: "Flags",
                render: (r) => (
                  <div className="flex flex-wrap gap-1.5">
                    {r.emergency_contact && <span className="rounded-full bg-[#FFEBEE] px-2 py-0.5 text-[10px] font-semibold text-[#C62828]">Emergency</span>}
                    {r.legal_guardian && <span className="rounded-full bg-[#E3F2FD] px-2 py-0.5 text-[10px] font-semibold text-[#1565C0]">Guardian</span>}
                    {r.release_of_info && <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-semibold text-[#2E7D32]">ROI</span>}
                  </div>
                )},
              ...(canEditFlag ? [{
                key: "_actions", label: "Actions",
                render: (r) => (
                  <button type="button" data-testid={`ee-family-edit-${r.id}`} onClick={() => openEdit(r)}
                          aria-label="Edit"
                          className="h-8 w-8 rounded-full text-[#6D4C41] hover:bg-[#F5F2EB] flex items-center justify-center">
                    <Pencil size={14} />
                  </button>
                )
              }] : []),
            ]} empty="No family members recorded for this client yet." />
        </Card>
      ) : (
        <div>
          <div className="mb-3 flex items-center gap-3">
            <div className="text-sm text-[#7B6157]">Tenant-wide roll-up. Pick a client above to edit contacts.</div>
            <div className="relative ml-auto">
              <Search size={15} className="absolute left-3 top-3 text-[#84675B]" />
              <FInput data-testid="ee-family-rollup-search" placeholder="Search here"
                      className="pl-9 h-10 w-64" value={search}
                      onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <Card className="p-0">
            <DataTable rows={filteredRollup}
              headers={[
                { key: "client_name", label: "Client" },
                { key: "first_name", label: "First Name" },
                { key: "last_name", label: "Last Name" },
                { key: "relationship", label: "Relationship" },
                { key: "phone", label: "Phone" },
                { key: "email", label: "Email" },
              ]} empty="No family contacts have been recorded yet." />
          </Card>
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)}
                  title={editId ? "Edit Family Member" : "Add Family Member"}
                  width="max-w-xl" testId="ee-family-panel">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>First Name *</FLabel>
              <FInput data-testid="fam-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
            </div>
            <div><FLabel>Last Name *</FLabel>
              <FInput data-testid="fam-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
            </div>
            <div><FLabel>Relationship</FLabel>
              <FInput data-testid="fam-rel" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} placeholder="e.g., Mother, Spouse" />
            </div>
            <div><FLabel>Phone</FLabel>
              <FInput data-testid="fam-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div><FLabel>Email</FLabel>
              <FInput type="email" data-testid="fam-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div><FLabel>Address</FLabel>
              <FInput data-testid="fam-addr" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="col-span-2 flex flex-wrap gap-3">
              <CheckBox testId="fam-emergency" checked={!!form.emergency_contact}
                        onChange={() => setForm((s) => ({ ...s, emergency_contact: !s.emergency_contact }))}
                        label="Emergency Contact" />
              <CheckBox testId="fam-guardian" checked={!!form.legal_guardian}
                        onChange={() => setForm((s) => ({ ...s, legal_guardian: !s.legal_guardian }))}
                        label="Legal Guardian" />
              <CheckBox testId="fam-roi" checked={!!form.release_of_info}
                        onChange={() => setForm((s) => ({ ...s, release_of_info: !s.release_of_info }))}
                        label="Release of Info on file" />
            </div>
            <div className="col-span-2"><FLabel>Notes</FLabel>
              <FTextarea data-testid="fam-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Btn type="submit" data-testid="fam-save" disabled={busy}>{busy ? "Saving..." : "Save"}</Btn>
          </div>
        </form>
      </SlidePanel>
    </div>
  );
}

/* ============ Care Providers (Provider Directory) ============ */
function CareProvidersTab({ canEditFlag, isAdmin }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [panel, setPanel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const EMPTY_PROV = { name: "", role: "", organization: "", specialty: "",
                        phone: "", email: "", fax: "", npi: "", address: "",
                        in_network: false, accepting_new_patients: true, notes: "" };
  const [form, setForm] = useState(EMPTY_PROV);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/provider-directory", { params: { q: search || undefined } })
    .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [search]);

  const openAdd = () => { setForm(EMPTY_PROV); setEditId(null); setPanel(true); };
  const openEdit = (r) => {
    const merged = { ...EMPTY_PROV };
    Object.keys(EMPTY_PROV).forEach((k) => { if (r[k] !== undefined) merged[k] = r[k]; });
    setForm(merged); setEditId(r.id); setPanel(true);
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/provider-directory/${editId}`, form);
      else await api.post("/provider-directory", form);
      toast.success(editId ? "Provider updated" : "Provider added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/provider-directory/${confirmDel.id}`);
      toast.success("Provider removed");
      setConfirmDel(null); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div>
      <Toolbar search={search} setSearch={setSearch}
               canEdit={isAdmin}
               onAdd={openAdd} addLabel="Add New Provider"
               count={rows.length} />
      <Card className="p-0">
        <DataTable rows={rows}
          headers={[
            { key: "name", label: "Name" },
            { key: "role", label: "Role" },
            { key: "organization", label: "Organization" },
            { key: "specialty", label: "Specialty" },
            { key: "phone", label: "Phone" },
            { key: "email", label: "Email" },
            { key: "in_network", label: "In-Network",
              render: (r) => r.in_network ? (
                <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-semibold text-[#2E7D32]">In-Network</span>
              ) : <span className="text-[#84675B]">-</span> },
            ...(isAdmin ? [{ key: "_actions", label: "Actions",
              render: (r) => (
                <div className="flex gap-2">
                  <button type="button" data-testid={`prov-edit-${r.id}`} onClick={() => openEdit(r)}
                          aria-label="Edit"
                          className="h-8 w-8 rounded-full text-[#6D4C41] hover:bg-[#F5F2EB] flex items-center justify-center">
                    <Pencil size={14} />
                  </button>
                  <button type="button" data-testid={`prov-delete-${r.id}`} onClick={() => setConfirmDel(r)}
                          aria-label="Delete"
                          className="h-8 w-8 rounded-full text-[#C62828] hover:bg-[#FFEBEE] flex items-center justify-center">
                    <Trash2 size={14} />
                  </button>
                </div>
              )}] : [])
          ]} empty="No providers in the directory yet." />
      </Card>

      <SlidePanel open={panel} onClose={() => setPanel(false)}
                  title={editId ? "Edit Provider" : "Add New Provider"}
                  width="max-w-2xl" testId="ee-panel-care-providers">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>Name *</FLabel>
              <FInput data-testid="prov-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div><FLabel>Role *</FLabel>
              <FInput data-testid="prov-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g., Psychiatrist" required />
            </div>
            <div><FLabel>Organization</FLabel>
              <FInput data-testid="prov-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} />
            </div>
            <div><FLabel>Specialty</FLabel>
              <FInput data-testid="prov-spec" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} />
            </div>
            <div><FLabel>Phone</FLabel>
              <FInput data-testid="prov-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div><FLabel>Fax</FLabel>
              <FInput data-testid="prov-fax" value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} />
            </div>
            <div><FLabel>Email</FLabel>
              <FInput type="email" data-testid="prov-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div><FLabel>NPI</FLabel>
              <FInput data-testid="prov-npi" value={form.npi} onChange={(e) => setForm({ ...form, npi: e.target.value })} />
            </div>
            <div className="col-span-2"><FLabel>Address</FLabel>
              <FInput data-testid="prov-addr" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="flex flex-wrap gap-3 col-span-2">
              <CheckBox testId="prov-in-network" checked={!!form.in_network}
                        onChange={() => setForm((s) => ({ ...s, in_network: !s.in_network }))}
                        label="In-Network" />
              <CheckBox testId="prov-accepting" checked={!!form.accepting_new_patients}
                        onChange={() => setForm((s) => ({ ...s, accepting_new_patients: !s.accepting_new_patients }))}
                        label="Accepting New Patients" />
            </div>
            <div className="col-span-2"><FLabel>Notes</FLabel>
              <FTextarea data-testid="prov-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Btn type="submit" data-testid="prov-save" disabled={busy}>{busy ? "Saving..." : "Save"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={doDelete} busy={busy}
                     title="Remove this provider"
                     description="This removes the provider from the tenant directory. Existing chart attachments stay intact."
                     confirmWord="DELETE" confirmLabel="Remove" />
    </div>
  );
}

/* ============ Main Page ============ */
export default function ExternalEntities() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canClinical = canEdit(user, "clinical");
  const [tab, setTab] = useState(() => {
    const stored = typeof window !== "undefined" && window.localStorage.getItem("ee.activeTab");
    return TABS.some((t) => t.key === stored) ? stored : "community-partners";
  });

  useEffect(() => {
    try { window.localStorage.setItem("ee.activeTab", tab); } catch (e) { /* no-op */ }
  }, [tab]);

  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];

  return (
    <div>
      <PageHeader title="External Entities"
                  subtitle="Tenant-wide directories of partners, providers, and referral resources." />

      <div className="mb-5 flex flex-wrap items-center gap-2 rounded-2xl bg-white border border-[#EFEBE0] p-2 shadow-[0_4px_20px_-8px_rgba(78,52,46,0.08)]"
           data-testid="ee-tabs">
        {TABS.map((t) => (
          <TabButton key={t.key} tab={t} active={tab === t.key} onClick={() => setTab(t.key)} />
        ))}
      </div>

      <div data-testid={`ee-panel-view-${tab}`}>
        {tab === "community-partners" && (
          <CrudEntity tabKey="community-partners"
                      endpoint="/community-partners"
                      addLabel="Create New Community Partner"
                      emptyForm={EMPTY_CP} seed
                      FormComponent={CommunityPartnerForm}
                      isAdmin={isAdmin} canEditFlag={canClinical}
                      tableHeaders={[
                        { key: "agency_name", label: "Agency Name" },
                        { key: "type_of_partner", label: "Type of Partner" },
                        { key: "contact_person", label: "Contact Person" },
                        { key: "agency_title", label: "Agency Title" },
                        { key: "agency_address", label: "Agency Address" },
                        { key: "agency_website", label: "Agency Website" },
                        { key: "business_phone", label: "Business Phone" },
                        { key: "cell_phone", label: "Cell Phone" },
                        { key: "email", label: "Email Address" },
                      ]} />
        )}

        {tab === "collateral-contacts" && (
          <CrudEntity tabKey="collateral-contacts"
                      endpoint="/collateral-contacts"
                      addLabel="Add New Collateral Contact"
                      emptyForm={EMPTY_CC} seed
                      FormComponent={CollateralContactForm}
                      isAdmin={isAdmin} canEditFlag={canClinical}
                      tableHeaders={[
                        { key: "contact_name", label: "Contact Name" },
                        { key: "relationship", label: "Relationship" },
                        { key: "organization", label: "Organization" },
                        { key: "role", label: "Role" },
                        { key: "phone", label: "Phone" },
                        { key: "email", label: "Email" },
                        { key: "release_of_info_on_file", label: "ROI",
                          render: (r) => r.release_of_info_on_file
                            ? <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-semibold text-[#2E7D32]">On file</span>
                            : <span className="text-[#84675B]">-</span> },
                      ]} />
        )}

        {tab === "referral-resources" && (
          <CrudEntity tabKey="referral-resources"
                      endpoint="/referral-resources"
                      addLabel="Add New Referral Resource"
                      emptyForm={EMPTY_RR} seed
                      FormComponent={ReferralResourceForm}
                      isAdmin={isAdmin} canEditFlag={canClinical}
                      tableHeaders={[
                        { key: "resource_name", label: "Resource Name" },
                        { key: "service_type", label: "Service Type" },
                        { key: "contact_person", label: "Contact Person" },
                        { key: "phone", label: "Phone" },
                        { key: "email", label: "Email" },
                        { key: "website", label: "Website" },
                        { key: "in_network", label: "In-Network",
                          render: (r) => r.in_network
                            ? <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-semibold text-[#2E7D32]">Yes</span>
                            : <span className="text-[#84675B]">No</span> },
                        { key: "accepts_medicaid", label: "Medicaid",
                          render: (r) => r.accepts_medicaid
                            ? <span className="rounded-full bg-[#E3F2FD] px-2 py-0.5 text-[10px] font-semibold text-[#1565C0]">Yes</span>
                            : <span className="text-[#84675B]">No</span> },
                      ]} />
        )}

        {tab === "care-providers" && (
          <CareProvidersTab isAdmin={isAdmin} canEditFlag={canClinical} />
        )}

        {tab === "family" && <FamilyTab canEditFlag={canClinical} />}

        {tab === "assigned-providers" && (
          <ReportTab endpoint="/external-entities/assigned-providers" tabKey={tab}
                     tableHeaders={[
                       { key: "name", label: "Provider" },
                       { key: "role", label: "Role" },
                       { key: "organization", label: "Organization" },
                       { key: "specialty", label: "Specialty" },
                       { key: "assigned_client_count", label: "Assigned Clients",
                         render: (r) => (
                           <span className="rounded-full bg-[#F5F2EB] px-2.5 py-0.5 text-xs font-semibold text-[#4E342E]">
                             {r.assigned_client_count}
                           </span>
                         )},
                       { key: "assigned_clients", label: "Client Names",
                         render: (r) => (r.assigned_clients || []).join(", ") || <span className="text-[#84675B]">-</span> },
                       { key: "phone", label: "Phone" },
                     ]} />
        )}

        {tab === "care-providers-report" && (
          <ReportTab endpoint="/external-entities/care-providers-report" tabKey={tab}
                     tableHeaders={[
                       { key: "client_name", label: "Client" },
                       { key: "name", label: "Provider" },
                       { key: "role", label: "Role" },
                       { key: "organization", label: "Organization" },
                       { key: "phone", label: "Phone" },
                       { key: "email", label: "Email" },
                       { key: "npi", label: "NPI" },
                       { key: "is_primary", label: "Primary",
                         render: (r) => r.is_primary
                           ? <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-semibold text-[#2E7D32]">Primary</span>
                           : <span className="text-[#84675B]">-</span> },
                     ]} />
        )}

        {tab === "collateral-contacts-report" && (
          <ReportTab endpoint="/collateral-contacts" tabKey={tab}
                     tableHeaders={[
                       { key: "contact_name", label: "Contact Name" },
                       { key: "relationship", label: "Relationship" },
                       { key: "organization", label: "Organization" },
                       { key: "role", label: "Role" },
                       { key: "phone", label: "Phone" },
                       { key: "email", label: "Email" },
                       { key: "address", label: "Address" },
                       { key: "release_of_info_on_file", label: "ROI",
                         render: (r) => r.release_of_info_on_file
                           ? <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-semibold text-[#2E7D32]">On file</span>
                           : <span className="text-[#84675B]">-</span> },
                     ]} />
        )}

        {tab === "community-partners-report" && (
          <ReportTab endpoint="/community-partners" tabKey={tab}
                     tableHeaders={[
                       { key: "agency_name", label: "Agency Name" },
                       { key: "type_of_partner", label: "Type of Partner" },
                       { key: "contact_person", label: "Contact Person" },
                       { key: "agency_title", label: "Agency Title" },
                       { key: "agency_address", label: "Agency Address" },
                       { key: "agency_website", label: "Agency Website" },
                       { key: "business_phone", label: "Business Phone" },
                       { key: "cell_phone", label: "Cell Phone" },
                       { key: "email", label: "Email Address" },
                     ]} />
        )}

        {tab === "family-contact-report" && (
          <ReportTab endpoint="/external-entities/family-report" tabKey={tab}
                     tableHeaders={[
                       { key: "client_name", label: "Client" },
                       { key: "first_name", label: "First Name" },
                       { key: "last_name", label: "Last Name" },
                       { key: "relationship", label: "Relationship" },
                       { key: "phone", label: "Phone" },
                       { key: "email", label: "Email" },
                       { key: "address", label: "Address" },
                       { key: "_flags", label: "Flags",
                         render: (r) => (
                           <div className="flex flex-wrap gap-1.5">
                             {r.emergency_contact && <span className="rounded-full bg-[#FFEBEE] px-2 py-0.5 text-[10px] font-semibold text-[#C62828]">Emergency</span>}
                             {r.legal_guardian && <span className="rounded-full bg-[#E3F2FD] px-2 py-0.5 text-[10px] font-semibold text-[#1565C0]">Guardian</span>}
                             {r.release_of_info && <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-semibold text-[#2E7D32]">ROI</span>}
                           </div>
                         )},
                     ]} />
        )}

        {tab === "insurance-companies-report" && (
          <ReportTab endpoint="/external-entities/insurance-companies-report" tabKey={tab}
                     tableHeaders={[
                       { key: "name", label: "Insurance Company" },
                       { key: "plan_type", label: "Plan Type" },
                       { key: "payer_id", label: "Payer ID" },
                       { key: "phone", label: "Phone" },
                       { key: "fax", label: "Fax" },
                       { key: "email", label: "Email" },
                       { key: "website", label: "Website" },
                       { key: "insured_count", label: "Insured",
                         render: (r) => (
                           <span className="rounded-full bg-[#F5F2EB] px-2.5 py-0.5 text-xs font-semibold text-[#4E342E]">
                             {r.insured_count}
                           </span>
                         )},
                       { key: "status", label: "Status",
                         render: (r) => (
                           <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.status === "archived" ? "bg-[#F5F2EB] text-[#7B6157]" : "bg-[#E8F5E9] text-[#2E7D32]"}`}>
                             {r.status || "active"}
                           </span>
                         )},
                     ]} />
        )}

        {tab === "referral-resources-report" && (
          <ReportTab endpoint="/referral-resources" tabKey={tab}
                     tableHeaders={[
                       { key: "resource_name", label: "Resource" },
                       { key: "service_type", label: "Service Type" },
                       { key: "contact_person", label: "Contact Person" },
                       { key: "phone", label: "Phone" },
                       { key: "email", label: "Email" },
                       { key: "website", label: "Website" },
                       { key: "address", label: "Address" },
                       { key: "in_network", label: "In-Network",
                         render: (r) => r.in_network
                           ? <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-semibold text-[#2E7D32]">Yes</span>
                           : <span className="text-[#84675B]">No</span> },
                       { key: "accepts_medicaid", label: "Medicaid",
                         render: (r) => r.accepts_medicaid
                           ? <span className="rounded-full bg-[#E3F2FD] px-2 py-0.5 text-[10px] font-semibold text-[#1565C0]">Yes</span>
                           : <span className="text-[#84675B]">No</span> },
                     ]} />
        )}
      </div>
    </div>
  );
}
